import { useEffect, useMemo, useState } from 'react';
import { describeKenoTicket, type KenoRules, type PublicKenoTicket } from '@tv-casino/shared';
import { KenoBoard } from './KenoBoard.js';
import { FloatAction, useOffscreenWatch } from './FloatAction.js';
import { kenoPayRows, kenoQuickStakes, ticketColor, ticketResultText } from '@tv-casino/viewmodels/components/kenoBoardModel.js';
import { kenoCommitFloat } from '@tv-casino/viewmodels/components/floatAction.js';
import styles from './KenoBet.module.css';

interface KenoBetProps {
  rules: KenoRules;
  /** Chips free to stake, outside anything already on the board. */
  chips: number;
  /**
   * This seat's written tickets, oldest first. `colorIndex` is the ticket's
   * table colour (what the TV paints it); without it the list position is used.
   */
  tickets: (PublicKenoTicket & { colorIndex?: number })[];
  /** The previous draw's slate, for the rebet button. Empty before a draw. */
  lastTickets: { spots: number[]; amount: number }[];
  /** Total currently staked by this seat. */
  staked: number;
  /** True once the seat locked in with Done. */
  done: boolean;
  /**
   * False outside the betting window: the board stays up with the draw and
   * this seat's marks, but no new tickets can be written.
   */
  canDrop?: boolean;
  busy: boolean;
  onAdd: (spots: number[], amount: number) => void;
  /**
   * Rewrite one written ticket in place — new spots, new stake, same seat.
   * Omitted (or unavailable) leaves the rows read-only.
   */
  onEdit?: (ticketId: number, spots: number[], amount: number) => void;
  /** Take one ticket back, or the whole slate when no id is given. */
  onClear: (ticketId?: number) => void;
  onRebet?: () => void;
  onDone: () => void;
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * The phone's keno card: one board that always shows the slate in its ticket
 * colours, with the ticket being written dashed over the top. Tap spots, pick
 * a stake, Add ticket; the list below pulls tickets one at a time and Done
 * locks the slate in.
 */
export function KenoBet({
  rules,
  chips,
  tickets,
  lastTickets,
  staked,
  done,
  canDrop = true,
  busy,
  onAdd,
  onEdit,
  onClear,
  onRebet,
  onDone,
}: KenoBetProps) {
  const [tapped, setPicked] = useState<number[]>([]);
  const [chosenStake, setStake] = useState(rules.minBet);
  const [editingId, setEditing] = useState<number | null>(null);
  // The host can move the limits between draws while this panel stays
  // mounted, so the armed stake and the tapped spots always read through the
  // current rules rather than the ones they were chosen under.
  const stake = Math.min(rules.maxBet, Math.max(rules.minBet, chosenStake));
  const picked = tapped.slice(0, rules.maxSpots);
  const writing = canDrop && !done;
  useEffect(() => {
    if (!writing) {
      setPicked([]);
      setEditing(null);
    }
  }, [writing]);
  const editingTicket = editingId === null ? undefined : tickets.find((t) => t.id === editingId);
  // The ticket being rewritten vanished (pulled by its ✕, the slate swept,
  // the draw landed): fall back to writing a fresh one.
  useEffect(() => {
    if (editingId !== null && !tickets.some((t) => t.id === editingId)) setEditing(null);
  }, [editingId, tickets]);
  const editable = writing && onEdit !== undefined;
  const editingActive = editable && editingTicket !== undefined;
  const quickStakes = useMemo(() => kenoQuickStakes(rules.minBet, rules.maxBet), [rules.minBet, rules.maxBet]);
  const boardTickets = useMemo(
    () => tickets.map((ticket, index) => ({ spots: ticket.spots, colorIndex: ticket.colorIndex ?? index })),
    [tickets],
  );
  const slateFull = tickets.length >= rules.maxTickets;
  const spotsLeft = Math.max(0, rules.maxSpots - picked.length);
  // Outside an edit the whole stake must fit the free chips; inside one the
  // old stake is still debited, so only the raise does.
  const stakeDelta = editingTicket ? stake - editingTicket.amount : stake;
  const affordable = stakeDelta <= chips;
  const addable = writing && !editingActive && !slateFull && picked.length >= 1 && affordable;
  const updatable = editingActive && picked.length >= 1 && affordable;
  const rebetTotal = lastTickets.reduce((sum, ticket) => sum + ticket.amount, 0);
  // Rebet replays the whole last slate onto an empty card. The window already
  // opens with that slate down, so offering it over a live slate would only
  // ever stack a duplicate of every ticket.
  const rebetOffered = writing && onRebet !== undefined && lastTickets.length > 0 && tickets.length === 0;
  const rebettable = lastTickets.length <= rules.maxTickets && rebetTotal <= chips;
  // The pay card re-prices on every tap and every stake change, like the
  // printed rate card beside a paper ticket.
  const payRows = useMemo(
    () => kenoPayRows(rules.paytable, picked.length, stake),
    [rules.paytable, picked.length, stake],
  );

  /**
   * The board is the tallest panel in the house, so the commit lives below
   * the fold whenever a ticket is being written — both halves float, craps'
   * roll being the pattern. The pad's own tap (Add, or Update under an edit)
   * wins while a draft stands; with the slate down and nothing staged, Done
   * is the tap the screen owes.
   */
  const commit = kenoCommitFloat({
    writing,
    addable,
    updatable,
    editing: editingActive,
    stake,
    staked,
  });
  const commitWatch = useOffscreenWatch<HTMLButtonElement>(commit.commit !== null);
  const doneWatch = useOffscreenWatch<HTMLButtonElement>(commit.done !== null);

  const toggle = (spot: number) => {
    if (!writing) return;
    setPicked((prev) => {
      const current = prev.slice(0, rules.maxSpots);
      if (current.includes(spot)) return current.filter((s) => s !== spot);
      return current.length >= rules.maxSpots ? current : [...current, spot];
    });
  };

  const add = () => {
    if (!addable) return;
    onAdd([...picked].sort((a, b) => a - b), stake);
    setPicked([]);
  };

  // Load a written ticket back onto the pad: its spots and stake become the
  // draft, dashed over the ticket it is rewriting until Update lands.
  const startEdit = (ticket: PublicKenoTicket) => {
    if (!editable) return;
    if (editingId === ticket.id) {
      // Tapping the ticket under edit again puts the pad down.
      setEditing(null);
      setPicked([]);
      return;
    }
    setPicked([...ticket.spots]);
    setStake(ticket.amount);
    setEditing(ticket.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setPicked([]);
  };

  const update = () => {
    if (!updatable || editingId === null) return;
    onEdit(editingId, [...picked].sort((a, b) => a - b), stake);
    setPicked([]);
    setEditing(null);
  };

  const takeBackVisible = canDrop && staked > 0;

  return (
    <section className={styles.wrap}>
      <p className={styles.heading}>
        {done
          ? 'Keno — locked in'
          : canDrop
            ? `Keno — tap ${rules.maxSpots === 1 ? 'a spot' : `up to ${rules.maxSpots} spots`}`
            : 'Keno — the board'}
      </p>

      {/* The slate paints in ticket colours; the draft ticket dashes over it. */}
      {writing && (
        <KenoBoard
          draw={null}
          tickets={boardTickets}
          draft={picked}
          onSpot={toggle}
          classicColors={rules.classicColors}
        />
      )}

      {writing && (
        <div className={styles.rail} role="group" aria-label="Stake">
          {quickStakes.map((amount) => (
            <button
              key={amount}
              type="button"
              className={cx(styles.stakeChip, stake === amount && styles.stakeOn, amount > chips && styles.stakeOff)}
              aria-pressed={stake === amount}
              aria-label={`${amount} chip stake`}
              onClick={() => setStake(amount)}
            >
              {amount}
            </button>
          ))}
          <span className={styles.hint}>
            {!affordable
              ? 'Not enough chips for that stake'
              : spotsLeft === 0
              ? editingActive
                ? 'Ticket full — update it'
                : 'Ticket full — add it'
              : slateFull && !editingActive
              ? `Slate full — ${rules.maxTickets} tickets`
              : `${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left`}
          </span>
        </div>
      )}

      {writing && payRows.length > 0 && (
        <table
          className={styles.pays}
          aria-label={`${picked.length}-spot pays at ${stake.toLocaleString()} chips`}
        >
          <caption className={styles.paysHead}>
            {picked.length}-spot pays · {stake.toLocaleString()} to play
          </caption>
          <thead>
            <tr>
              <th scope="col">Catch</th>
              <th scope="col">Pays</th>
            </tr>
          </thead>
          <tbody>
            {payRows.map((row) => (
              <tr key={row.catches} className={row.pays > stake ? styles.payWin : undefined}>
                <td>{row.catches}</td>
                <td>{row.pays.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {writing && (
        <div className={styles.actions}>
          <button
            ref={commitWatch.ref}
            type="button"
            className={styles.add}
            disabled={busy || !(editingActive ? updatable : addable)}
            onClick={editingActive ? update : add}
          >
            {editingActive ? 'Update ticket' : 'Add ticket'} ({stake.toLocaleString()})
          </button>
          <button
            type="button"
            className={styles.sweep}
            disabled={picked.length === 0}
            onClick={() => setPicked([])}
          >
            Clear spots
          </button>
          {editingActive && (
            <button type="button" className={styles.sweep} onClick={cancelEdit}>
              Cancel edit
            </button>
          )}
        </div>
      )}

      {(writing || takeBackVisible || done) && (
        <div className={styles.actions}>
          {rebetOffered && (
            <button
              type="button"
              className={styles.sweep}
              disabled={busy || !rebettable}
              onClick={onRebet}
              aria-label={`Rebet the previous slate, ${rebetTotal} chips`}
            >
              Rebet last ({rebetTotal.toLocaleString()})
            </button>
          )}
          {takeBackVisible && (
            <button
              type="button"
              className={styles.sweep}
              disabled={busy}
              onClick={() => onClear()}
            >
              Take back all ({staked.toLocaleString()})
            </button>
          )}
          {(writing || done) && (
            <button
              ref={doneWatch.ref}
              type="button"
              className={done ? styles.doneOn : styles.done}
              disabled={busy || !canDrop || staked === 0 || done}
              onClick={onDone}
            >
              {done ? 'Locked in' : 'Done — lock in'}
            </button>
          )}
        </div>
      )}

      {/* Never both: `kenoCommitFloat` owes the pad's own tap while a draft
          stands, and Done once the slate is down with nothing staged. */}
      <FloatAction
        show={commit.commit !== null && commitWatch.offscreen}
        buttons={
          commit.commit
            ? [
                {
                  label: commit.commit.label,
                  disabled: busy,
                  onClick: editingActive ? update : add,
                },
              ]
            : []
        }
      />
      <FloatAction
        show={commit.done !== null && doneWatch.offscreen}
        buttons={
          commit.done ? [{ label: commit.done.label, disabled: busy, onClick: onDone }] : []
        }
      />

      {tickets.length > 0 && (
        <ul className={styles.tickets} aria-label="Your tickets">
          {tickets.map((ticket, index) => {
            const result = ticketResultText(ticket.catches, ticket.amount, ticket.payout);
            const returned = ticket.payout != null && ticket.payout >= ticket.amount;
            const color = ticketColor(ticket.colorIndex ?? index, rules.classicColors);
            const lines = (
              <>
                <span className={styles.swatch} style={{ background: color }} aria-hidden="true" />
                <span className={styles.ticketLines}>
                  <span className={styles.ticketLabel}>{describeKenoTicket(ticket.spots)}</span>
                  <span className={styles.ticketMeta}>
                    {ticket.amount.toLocaleString()}
                    {result && (
                      <span className={returned ? styles.ticketGold : styles.ticketDim}>
                        {' · '}
                        {result}
                      </span>
                    )}
                  </span>
                </span>
              </>
            );
            return (
              <li
                key={ticket.id}
                className={cx(styles.ticketRow, editingId === ticket.id && editable && styles.ticketEditing)}
              >
                {editable ? (
                  <button
                    type="button"
                    className={styles.ticketPick}
                    aria-pressed={editingId === ticket.id}
                    onClick={() => startEdit(ticket)}
                    aria-label={`Edit ${describeKenoTicket(ticket.spots)}, ${ticket.amount} chips`}
                  >
                    {lines}
                  </button>
                ) : (
                  lines
                )}
                {canDrop && (
                  <button
                    type="button"
                    className={styles.ticketClear}
                    disabled={busy}
                    onClick={() => onClear(ticket.id)}
                    aria-label={`Take back ${ticket.amount} on ${describeKenoTicket(ticket.spots)}`}
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {writing && (
        <p className={styles.hint}>
          {editingActive
            ? 'Rewriting that ticket — move its spots or stake, then Update. Cancel puts the pad back down.'
            : tickets.length > 0
              ? 'Each colour is one ticket. Tap one to rewrite it, tap spots to write another, or Done to lock in.'
              : 'Tap spots to fill a ticket, then Add ticket. Tap a spot again to take it off. Done locks your slate in for the draw.'}
        </p>
      )}
    </section>
  );
}
